import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import storage from 'helpers/storage';
import { general_error_toastr, general_form_error_toastr } from 'components/partials/Notification';
import { APP_CONFIG } from 'Config';
import { FACILITY_MANAGER } from 'helpers/users';
import transformMarketingPreferences from '../_helpers/transformMarketingPreferences';
import { useGlobalContext } from 'context/GlobalContext';
import userTypeUrlMap from '../_helpers/userTypeUrlMap';
import { FormValues, Question, UseRegisterFormProps } from '../types';
import transformCheckboxBooleans from 'components/Register/_helpers/transformCheckboxBooleans';

const useRegisterForm = ({
    regTypeId,
    getPMSSystems,
    getHeardQuestions,
    getSecurityQuestions,
    getIDVerificationQuestions,
    regFormSaveStep1,
    csrf_token,
    handleRegInputChange,
    clearRegForm,
}: UseRegisterFormProps) => {
    const router = useRouter();
    const { countries } = useGlobalContext() as any;
    const [userData, setUserData] = useState<FormValues>(null);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [userId, setUserId] = useState<string | null>(null);
    const [verification, setVerification] = useState<boolean>(false);

    useEffect(() => {
        const auth_token = storage.getItem('st.authed') || false;

        if (auth_token && auth_token === '1') {
            router.push('/account/dashboard');
        }

        getPMSSystems();
        getHeardQuestions();
        getSecurityQuestions();
    }, []);

    const getQuestions = async ({ part = 1, user = userData }: { part?: number, user: FormValues }) => {
        let questions: Question[] = [];

        const stateCode = countries
            .find(c => c.country_id === Number(user.country_id))
            ?.states
            .find(s => s.state_id === Number(user.state_id))
            ?.state_code;

        try {
            const { questions: _questions } = await getIDVerificationQuestions({
                phone: user.phone || '',
                street: user.address || '',
                city: user.city || '',
                state_abbrv: stateCode || '',
                postal_code: user.zipcode || '',
                birth_date: user.birthdate || '',
                first_name: user.fname || '',
                last_name: user.lname || '',
                ssn: user.ssn || '',
                part,
            });

            questions = _questions;

            if (questions.length) {
                setQuestions(questions);
            }
        } catch (e) {
            general_error_toastr();
        } finally {
            return questions;
        }
    };

    const lastStepRedirect = () => {
        router.push(`/register/${userTypeUrlMap[regTypeId]}/${regTypeId === FACILITY_MANAGER.id ? 'facility' : 'submitted'}`);
    };

    const updateReduxFormValues = (values: { [key: string]: any }) => {
        for (let key in values) {
            handleRegInputChange({ target: { name: key, value: values[key] } }, 'user');
        }
    };

    const handleSubmit = async (userData: FormValues) => {
        let _questions: Question[] = [];

        setUserData(userData);
        setLoading(true);
        updateReduxFormValues(userData);
        
        const requestPayload = { ...userData };
        
        transformMarketingPreferences(requestPayload);
        transformCheckboxBooleans(requestPayload);
        
        requestPayload.reg_type_id = String(regTypeId);

        if (regTypeId !== FACILITY_MANAGER.id && APP_CONFIG.REACT_APP_REQUIRE_IDENTITY) {
            if (requestPayload.country_id !== '37') {
                _questions = await getQuestions({ user: requestPayload });

                requestPayload.identity_status_slug = _questions.length ? 'questions' : 'photo';
            }

            requestPayload.identity_verified = false;
        }

        regFormSaveStep1(
            requestPayload,
            csrf_token,
        ).then(({ user_id, pending_match, existing_user }) => {
            localStorage.setItem('user_id', user_id);
            if (regTypeId === FACILITY_MANAGER.id) {
                lastStepRedirect();
                return null;
            }

            if (pending_match) {
                router.push(`/register/${userTypeUrlMap[regTypeId]}/${existing_user ? 'existing' : 'pending'}`);
                return null;
            }

            setUserId(user_id);

            if (APP_CONFIG.REACT_APP_REQUIRE_IDENTITY) {
                if (!_questions?.length) {
                    return lastStepRedirect();
                }
                if (regTypeId !== FACILITY_MANAGER.id && _questions) {
                    return setVerification(true);
                }
            }

            return lastStepRedirect();
        },
        () => {
            setLoading(false);
            general_form_error_toastr();
        });
    };

    const handleCancelForm = () => {
        clearRegForm();
        router.push('/');
    };

    return {
        loading,
        verification,
        questions,
        userId,
        getQuestions,
        lastStepRedirect,
        handleSubmit,
        handleCancelForm,
    };
};

export default useRegisterForm;